import { RouteId } from "@/types/routes";
import {
    User,
    CreditCard,
    MapPin,
    Mail,
    Phone,
    Calendar,
    Building,
    Banknote,
    Hash,
    Globe,
    Shield,
    Terminal,
    Key,
    Heart,
    Sparkles,
    Film,
    UserCheck,
    Zap,
    Skull,
} from 'lucide-react';
import { Locale } from "./locales";

export interface ICategory {
    id: RouteId;
    name: Record<Locale, string>;
    description: Record<Locale, string>;
    icon: React.ReactElement;
    generators: IGenerator[];
}

export interface IGenerator {
    id: RouteId;
    name: Record<Locale, string>;
    description: Record<Locale, string>;
    icon: React.ReactElement;
    color: string;
}

// Categories and generators configuration
export const CATEGORIES: Record<string, ICategory> = {
    personal: {
        id: 'personal',
        name: {
            en: 'Personal Data',
            es: 'Datos Personales',
            pt: 'Dados Pessoais',
            hi: 'व्यक्तिगत डेटा',
            ar: 'البيانات الشخصية',
            fr: 'Données Personnelles',
            de: 'Personenbezogene Daten',
            ja: '個人情報',
            tr: 'Kişisel Veriler',
            ko: '개인 데이터',
            id: 'Data Pribadi',
            vi: 'Dữ liệu cá nhân',
            ru: 'Персональные данные',
            it: 'Dati Personali',
            pl: 'Dane osobiste',
            nl: 'Persoonlijke Gegevens',
        },
        description: {
            en: 'Free fake name generator and personal data tools. Generate realistic fake names, addresses, emails, phone numbers and more for testing, development and prototyping',
            es: 'Herramientas gratuitas de generador de nombres falsos y datos personales. Genera nombres falsos realistas, direcciones, correos electrónicos, números de teléfono y más para pruebas, desarrollo y prototipos',
            pt: 'Ferramentas gratuitas de gerador de nomes falsos e dados pessoais. Gere nomes falsos realistas, endereços, e-mails, números de telefone e mais para testes, desenvolvimento e prototipagem',
            hi: 'मुफ्त फेक नाम जनरेटर और व्यक्तिगत डेटा टूल। टेस्टिंग, डेवलपमेंट और प्रोटोटाइपिंग के लिए वास्तविक फेक नाम, पते, ईमेल, फोन नंबर और अधिक उत्पन्न करें',
            ar: 'أدوات مجانية لمولد الأسماء المزيفة والبيانات الشخصية. إنشاء أسماء مزيفة حقيقية، عناوين، بريد إلكتروني، أرقام هواتف والمزيد للاختبار والتطوير والتصميم',
            fr: 'Outils gratuits de générateur de noms faux et de données personnelles. Générez des noms faux réalistes, des adresses, des e-mails, des numéros de téléphone et plus pour les tests, le développement et le prototypage',
            de: 'Kostenlose Fake Name Generator und persönliche Daten Tools. Generieren Sie realistische falsche Namen, Adressen, E-Mails, Telefonnummern und mehr für Tests, Entwicklung und Prototyping',
            ja: '無料の偽名ジェネレーターと個人データツール。テスト、開発、プロトタイプのために現実的な偽名、住所、メール、電話番号などを生成',
            tr: 'Ücretsiz sahte isim üretici ve kişisel veri araçları. Test, geliştirme ve prototipleme için gerçekçi sahte isimler, adresler, e-postalar, telefon numaraları ve daha fazlasını oluşturun',
            ko: '무료 가짜 이름 생성기 및 개인 데이터 도구. 테스트, 개발, 프로토타이핑을 위해 실제적인 가짜 이름, 주소, 이메일, 전화번호 등을 생성',
            id: 'Alat generator nama palsu dan data pribadi gratis. Menghasilkan nama palsu yang realistis, alamat, email, nomor telepon dan banyak lagi untuk pengujian, pengembangan, dan prototip',
            vi: 'Công cụ tạo tên giả và dữ liệu cá nhân miễn phí. Tạo tên giả thực tế, địa chỉ, email, số điện thoại và nhiều hơn nữa cho việc kiểm tra, phát triển và thử nghiệm',
            ru: 'Бесплатные инструменты генератора фальшивых имен и персональных данных. Генерируйте реалистичные фальшивые имена, адреса, электронные письма, номера телефонов и многое другое для тестирования, разработки и прототипирования',
            it: 'Strumenti gratuiti generatore di nomi falsi e dati personali. Genera nomi falsi realistici, indirizzi, email, numeri di telefono e molto altro per test, sviluppo e prototipazione',
            pl: 'Darmowe narzędzia generatora fałszywych imion i danych osobowych. Generuj realistyczne fałszywe imiona, adresy, e-maile, numery telefonów i wiele więcej do testowania, rozwoju i prototypowania',
            nl: 'Gratis nepnaam generator en persoonlijke gegevens tools. Genereer realistische nepnamen, adressen, e-mails, telefoonnummers en meer voor testen, ontwikkeling en prototypen',
        },
        icon: <User className="h-5 w-5" />,
        generators: [
            {
                id: 'names',
                name: {
                    en: 'Fake Name Generator',
                    es: 'Generador de Nombres Falsos',
                    pt: 'Gerador de Nomes Falsos',
                    hi: 'फॉल्स नाम जनरेटर',
                    ar: 'مولد الأسماء المزيفة',
                    fr: 'Générateur de Noms Faux',
                    de: 'Falschname Generator',
                    ja: '偽の名前ジェネレーター',
                    tr: 'Sahte Ad Üreticisi',
                    ko: '가짜 이름 생성기',
                    id: 'Pembangkit Nama Palsu',
                    vi: 'Bộ Tạo Tên Giả',
                    ru: 'Генератор Ложных Имен',
                    it: 'Generatore di Nomi Falsi',
                    pl: 'Generator Fałszywych Imion',
                    nl: 'Foutieve Naam Generator',
                },
                description: {
                    en: 'Free fake name generator. Generate realistic male names, female names, full names, first names, and last names instantly',
                    es: 'Generador de nombres falsos gratuito. Genera nombres masculinos, femeninos, completos, nombres y apellidos realistas al instante',
                    pt: 'Gerador de nomes falsos gratuito. Gere nomes masculinos, femininos, completos, nomes e sobrenomes realistas instantaneamente',
                    hi: 'मुफ्त फेक नाम जनरेटर। तुरंत वास्तविक पुरुष नाम, महिला नाम, पूरे नाम, पहले नाम और अंतिम नाम उत्पन्न करें',
                    ar: 'مولد أسماء مزيفة مجاني. إنشاء أسماء ذكور، إناث، أسماء كاملة، أسماء أولى وأسماء أخيرة حقيقية فوراً',
                    fr: 'Générateur de noms faux gratuit. Générez des noms masculins, féminins, complets, prénoms et noms de famille réalistes instantanément',
                    de: 'Kostenloser Fake Name Generator. Generieren Sie sofort realistische männliche Namen, weibliche Namen, vollständige Namen, Vornamen und Nachnamen',
                    ja: '無料の偽名ジェネレーター。即座に現実的な男性名、女性名、フルネーム、ファーストネーム、ラストネームを生成',
                    tr: 'Ücretsiz sahte isim üretici. Erkek isimleri, kadın isimleri, tam isimler, ilk isimler ve son isimleri anında oluşturun',
                    ko: '무료 가짜 이름 생성기. 실제적인 남성 이름, 여성 이름, 전체 이름, 이름, 성을 즉시 생성',
                    id: 'Generator nama palsu gratis. Menghasilkan nama pria, nama wanita, nama lengkap, nama depan, dan nama belakang secara instan',
                    vi: 'Trình tạo tên giả miễn phí. Tạo tên nam, tên nữ, tên đầy đủ, tên và họ ngay lập tức',
                    ru: 'Бесплатный генератор фальшивых имен. Генерируйте реалистичные мужские имена, женские имена, полные имена, имена и фамилии мгновенно',
                    it: 'Generatore di nomi falsi gratuito. Genera nomi maschili, femminili, completi, nomi e cognomi realistici istantaneamente',
                    pl: 'Darmowy generator fałszywych imion. Generuj realistyczne imiona męskie, żeńskie, pełne imiona, imiona i nazwiska natychmiast',
                    nl: 'Gratis nepnaam generator. Genereer direct realistische mannelijke namen, vrouwelijke namen, volledige namen, voornamen en achternamen',
                },
                icon: <User className="h-4 w-4" />,
                color: 'blue',
            },
            // {
            //     id: 'addresses',
            //     name: {
            //         en: 'Fake Address Generator',
            //         es: 'Generador de Direcciones Falsas',
            //         pt: 'Gerador de Endereços Falsos',
            //         hi: 'फेक पता जनरेटर',
            //         ar: 'مولد العناوين المزيفة',
            //         fr: 'Générateur d\'Adresses Fausses',
            //         de: 'Fake Adressgenerator',
            //         ja: '偽住所ジェネレーター',
            //         tr: 'Sahte Adres Üretici',
            //         ko: '가짜 주소 생성기',
            //         id: 'Generator Alamat Palsu',
            //         vi: 'Trình Tạo Địa Chỉ Giả',
            //         ru: 'Генератор Фальшивых Адресов',
            //         it: 'Generatore di Indirizzi Falsi',
            //         pl: 'Generator Fałszywych Adresów',
            //         nl: 'Nep Adres Generator',
            //     },
            //     description: {
            //         en: 'Generate realistic fake addresses including street, city, state, and zip codes for multiple countries. Perfect for form testing and data population.',
            //         es: 'Genere direcciones falsas realistas que incluyen calle, ciudad, estado y códigos postales para múltiples países. Perfecto para pruebas de formularios y población de datos.',
            //         pt: 'Gere endereços falsos realistas, incluindo rua, cidade, estado e códigos postais para vários países. Perfeito para testes de formulários e população de dados.',
            //         hi: 'कई देशों के लिए सड़क, शहर, राज्य और ज़िप कोड सहित वास्तविक फेक पते उत्पन्न करें। फॉर्म परीक्षण और डेटा आबादी के लिए बिल्कुल सही।',
            //         ar: 'قم بإنشاء عناوين مزيفة حقيقية بما في ذلك الشارع والمدينة والولاية والرموز البريدية لبلدان متعددة. مثالي لاختبار النماذج وملء البيانات.',
            //         fr: 'Générez des adresses fausses réalistes, y compris la rue, la ville, l\'état et les codes postaux pour plusieurs pays. Parfait pour les tests de formulaires et la population de données.',
            //         de: 'Generieren Sie realistische falsche Adressen mit Straße, Stadt, Bundesland und Postleitzahlen für mehrere Länder. Perfekt für Formulartests und Datenpopulation.',
            //         ja: '複数の国の通り、都市、州、郵便番号を含む現実的な偽の住所を生成します。フォームテストとデータ入力に最適です。',
            //         tr: 'Birden fazla ülke için sokak, şehir, eyalet ve posta kodları dahil olmak üzere gerçekçi sahte adresler oluşturun. Form testi ve veri doldurma için mükemmel.',
            //         ko: '여러 국가의 거리, 도시, 주 및 우편 번호를 포함한 현실적인 가짜 주소를 생성합니다. 양식 테스트 및 데이터 채우기에 완벽합니다.',
            //         id: 'Hasilkan alamat palsu yang realistis termasuk jalan, kota, negara bagian, dan kode pos untuk beberapa negara. Sempurna untuk pengujian formulir dan populasi data.',
            //         vi: 'Tạo địa chỉ giả thực tế bao gồm đường, thành phố, tiểu bang và mã bưu chính cho nhiều quốc gia. Hoàn hảo cho kiểm tra biểu mẫu và dân số dữ liệu.',
            //         ru: 'Генерируйте реалистичные фальшивые адреса, включая улицу, город, штат и почтовые индексы для нескольких стран. Идеально подходит для тестирования форм и заполнения данных.',
            //         it: 'Genera indirizzi falsi realistici tra cui via, città, stato e codici postali per più paesi. Perfetto per testare moduli e popolare dati.',
            //         pl: 'Generuj realistyczne fałszywe adresy, w tym ulicę, miasto, stan i kody pocztowe dla wielu krajów. Idealne do testowania formularzy i wypełniania danych.',
            //         nl: 'Genereer realistische nepadressen inclusief straat, stad, staat en postcodes voor meerdere landen. Perfect voor het testen van formulieren en datapopulatie.',
            //     },
            //     icon: <MapPin className="h-4 w-4" />,
            //     color: 'emerald',
            // },
            // {
            //     id: 'emails',
            //     name: {
            //         en: 'Fake Email Generator',
            //         es: 'Generador de Correos Electrónicos Falsos',
            //         pt: 'Gerador de E-mails Falsos',
            //         hi: 'फेक ईमेल जनरेटर',
            //         ar: 'مولد البريد الإلكتروني المزيف',
            //         fr: 'Générateur d\'E-mails Faux',
            //         de: 'Fake E-Mail Generator',
            //         ja: '偽メールジェネレーター',
            //         tr: 'Sahte E-posta Üretici',
            //         ko: '가짜 이메일 생성기',
            //         id: 'Generator Email Palsu',
            //         vi: 'Trình Tạo Email Giả',
            //         ru: 'Генератор Фальшивых Email',
            //         it: 'Generatore di Email False',
            //         pl: 'Generator Fałszywych Emaili',
            //         nl: 'Nep E-mail Generator',
            //     },
            //     description: {
            //         en: 'Generate realistic fake email addresses with various domains. Perfect for testing forms and applications.',
            //         es: 'Genere direcciones de correo electrónico falsas realistas con varios dominios. Perfecto para probar formularios y aplicaciones.',
            //         pt: 'Gere endereços de e-mail falsos realistas com vários domínios. Perfeito para testar formulários e aplicações.',
            //         hi: 'विभिन्न डोमेन के साथ यथार्थवादी फेक ईमेल पते उत्पन्न करें। फॉर्म और अनुप्रयोगों के परीक्षण के लिए बिल्कुल सही।',
            //         ar: 'قم بإنشاء عناوين بريد إلكتروني مزيفة واقعية مع نطاقات مختلفة. مثالي لاختبار النماذج والتطبيقات.',
            //         fr: 'Générez des adresses e-mail fausses réalistes avec divers domaines. Parfait pour tester les formulaires et les applications.',
            //         de: 'Generieren Sie realistische falsche E-Mail-Adressen mit verschiedenen Domains. Perfekt für Formulartests und Anwendungen.',
            //         ja: 'さまざまなドメインで現実的な偽のメールアドレスを生成します。フォームとアプリケーションのテストに最適です。',
            //         tr: 'Çeşitli domainlerle gerçekçi sahte e-posta adresleri oluşturun. Form ve uygulama testleri için mükemmel.',
            //         ko: '다양한 도메인으로 현실적인 가짜 이메일 주소를 생성합니다. 양식 및 애플리케이션 테스트에 완벽합니다.',
            //         id: 'Hasilkan alamat email palsu yang realistis dengan berbagai domain. Sempurna untuk menguji formulir dan aplikasi.',
            //         vi: 'Tạo địa chỉ email giả thực tế với nhiều miền khác nhau. Hoàn hảo cho việc kiểm tra biểu mẫu và ứng dụng.',
            //         ru: 'Генерируйте реалистичные фальшивые адреса электронной почты с различными доменами. Идеально для тестирования форм и приложений.',
            //         it: 'Genera indirizzi email falsi realistici con vari domini. Perfetto per testare moduli e applicazioni.',
            //         pl: 'Generuj realistyczne fałszywe adresy e-mail z różnymi domenami. Idealny do testowania formularzy i aplikacji.',
            //         nl: 'Genereer realistische nepadres e-mails met verschillende domeinen. Perfect voor het testen van formulieren en applicaties.',
            //     },
            //     icon: <Mail className="h-4 w-4" />,
            //     color: 'purple',
            // },
            {
                id: 'ip-addresses',
                name: {
                    en: 'IP Address Generator',
                    es: 'Generador de Direcciones IP',
                    pt: 'Gerador de Endereços IP',
                    hi: 'आईपी एड्रेस जनरेटर',
                    ar: 'مولد عناوين IP',
                    fr: 'Générateur d\'Adresses IP',
                    de: 'IP-Adresse Generator',
                    ja: 'IPアドレスジェネレーター',
                    tr: 'IP Adresi Üretici',
                    ko: 'IP 주소 생성기',
                    id: 'Generator Alamat IP',
                    vi: 'Trình Tạo Địa Chỉ IP',
                    ru: 'Генератор IP-адресов',
                    it: 'Generatore di Indirizzi IP',
                    pl: 'Generator Adresów IP',
                    nl: 'IP-adres Generator',
                },
                description: {
                    en: 'Generate random IPv4 and IPv6 addresses. Perfect for testing network applications and configurations.',
                    es: 'Genere direcciones IPv4 e IPv6 aleatorias. Perfecto para probar aplicaciones y configuraciones de red.',
                    pt: 'Gere endereços IPv4 e IPv6 aleatórios. Perfeito para testar aplicações e configurações de rede.',
                    hi: 'यादृच्छिक IPv4 और IPv6 पते उत्पन्न करें। नेटवर्क अनुप्रयोगों और कॉन्फ़िगरेशन के परीक्षण के लिए बिल्कुल सही।',
                    ar: 'إنشاء عناوين IPv4 و IPv6 عشوائية. مثالي لاختبار تطبيقات وتكوينات الشبكة.',
                    fr: 'Générez des adresses IPv4 et IPv6 aléatoires. Parfait pour tester les applications et configurations réseau.',
                    de: 'Generieren Sie zufällige IPv4- und IPv6-Adressen. Perfekt zum Testen von Netzwerkanwendungen und -konfigurationen.',
                    ja: 'ランダムなIPv4およびIPv6アドレスを生成します。ネットワークアプリケーションと構成のテストに最適です。',
                    tr: 'Rastgele IPv4 ve IPv6 adresleri üretin. Ağ uygulamalarını ve yapılandırmalarını test etmek için mükemmel.',
                    ko: '무작위 IPv4 및 IPv6 주소를 생성합니다. 네트워크 애플리케이션 및 구성 테스트에 완벽합니다.',
                    id: 'Hasilkan alamat IPv4 dan IPv6 acak. Sempurna untuk menguji aplikasi dan konfigurasi jaringan.',
                    vi: 'Tạo địa chỉ IPv4 và IPv6 ngẫu nhiên. Hoàn hảo để kiểm tra ứng dụng và cấu hình mạng.',
                    ru: 'Генерируйте случайные адреса IPv4 и IPv6. Идеально для тестирования сетевых приложений и конфигураций.',
                    it: 'Genera indirizzi IPv4 e IPv6 casuali. Perfetto per testare applicazioni e configurazioni di rete.',
                    pl: 'Generuj losowe adresy IPv4 i IPv6. Idealny do testowania aplikacji sieciowych i konfiguracji.',
                    nl: 'Genereer willekeurige IPv4- en IPv6-adressen. Perfect voor het testen van netwerkapplicaties en configuraties.',
                },
                icon: <Globe className="h-4 w-4" />,
                color: 'emerald',
            },
            {
                id: 'password-generator',
                name: {
                    en: 'Password Generator',
                    es: 'Generador de Contraseñas',
                    pt: 'Gerador de Senhas',
                    hi: 'पासवर्ड जनरेटर',
                    ar: 'مولد كلمات المرور',
                    fr: 'Générateur de Mots de Passe',
                    de: 'Passwort Generator',
                    ja: 'パスワードジェネレーター',
                    tr: 'Şifre Üretici',
                    ko: '비밀번호 생성기',
                    id: 'Generator Kata Sandi',
                    vi: 'Trình Tạo Mật Khẩu',
                    ru: 'Генератор Паролей',
                    it: 'Generatore di Password',
                    pl: 'Generator Haseł',
                    nl: 'Wachtwoord Generator',
                },
                description: {
                    en: 'Generate secure random passwords with custom rules. Include uppercase, lowercase, numbers, and symbols.',
                    es: 'Genere contraseñas aleatorias seguras con reglas personalizadas. Incluya mayúsculas, minúsculas, números y símbolos.',
                    pt: 'Gere senhas aleatórias seguras com regras personalizadas. Inclua maiúsculas, minúsculas, números e símbolos.',
                    hi: 'कस्टम नियमों के साथ सुरक्षित यादृच्छिक पासवर्ड उत्पन्न करें। अपरकेस, लोअरकेस, संख्याएं और प्रतीक शामिल करें।',
                    ar: 'إنشاء كلمات مرور عشوائية آمنة مع قواعد مخصصة. تشمل الأحرف الكبيرة والصغيرة والأرقام والرموز.',
                    fr: 'Générez des mots de passe aléatoires sécurisés avec des règles personnalisées. Incluez majuscules, minuscules, chiffres et symboles.',
                    de: 'Generieren Sie sichere zufällige Passwörter mit benutzerdefinierten Regeln. Großbuchstaben, Kleinbuchstaben, Zahlen und Symbole.',
                    ja: 'カスタムルールで安全なランダムパスワードを生成します。大文字、小文字、数字、記号を含めます。',
                    tr: 'Özel kurallarla güvenli rastgele şifreler üretin. Büyük harf, küçük harf, rakam ve semboller ekleyin.',
                    ko: '사용자 정의 규칙으로 안전한 무작위 비밀번호를 생성합니다. 대문자, 소문자, 숫자, 기호를 포함합니다.',
                    id: 'Hasilkan kata sandi acak yang aman dengan aturan khusus. Sertakan huruf besar, huruf kecil, angka, dan simbol.',
                    vi: 'Tạo mật khẩu ngẫu nhiên an toàn với các quy tắc tùy chỉnh. Bao gồm chữ hoa, chữ thường, số và ký hiệu.',
                    ru: 'Генерируйте безопасные случайные пароли с пользовательскими правилами. Включайте заглавные, строчные буквы, цифры и символы.',
                    it: 'Genera password casuali sicure con regole personalizzate. Includi maiuscole, minuscole, numeri e simboli.',
                    pl: 'Generuj bezpieczne losowe hasła z niestandardowymi regułami. Zawiera wielkie litery, małe litery, cyfry i symbole.',
                    nl: 'Genereer veilige willekeurige wachtwoorden met aangepaste regels. Inclusief hoofdletters, kleine letters, cijfers en symbolen.',
                },
                icon: <Key className="h-4 w-4" />,
                color: 'purple',
            },
            // {
            //     id: 'addresses',
            //     name: 'Addresses',
            //     description: 'Complete addresses with street, city, state, postal code',
            //     icon: <MapPin className="h-4 w-4" />,
            // },
            // {
            //     id: 'dates',
            //     name: 'Dates',
            //     description: 'Birth dates, random dates within specified ranges',
            //     icon: <Calendar className="h-4 w-4" />,
            // }
        ]
    },
    entertainment: {
        id: 'entertainment',
        name: {
            en: 'Entertainment and Cultural Generators',
            es: 'Generadores de Entretenimiento y Culturales',
            pt: 'Geradores de Entretenimento e Culturais',
            hi: 'मनोरंजन और सांस्कृतिक जेनरेटर',
            ar: 'مولدات الترفيه والثقافة',
            fr: 'Générateurs de Divertissement et Culturels',
            de: 'Unterhaltungs- und Kulturgeneratoren',
            ja: 'エンターテインメントと文化のジェネレーター',
            tr: 'Eğlence ve Kültürel Üreticiler',
            ko: '엔터테인먼트 및 문화 생성기',
            id: 'Generator Hiburan dan Budaya',
            vi: 'Trình tạo Giải trí và Văn hóa',
            ru: 'Генераторы Развлечений и Культуры',
            it: 'Generatori di Intrattenimento e Culturali',
            pl: 'Generatory Rozrywki i Kultury',
            nl: 'Entertainment en Culturele Generatoren',
        },
        description: {
            en: 'Generate creative names and identifiers for entertainment, gaming, and cultural purposes',
            es: 'Genere nombres e identificadores creativos para fines de entretenimiento, juegos y cultura',
            pt: 'Gere nomes e identificadores criativos para fins de entretenimento, jogos e cultura',
            hi: 'मनोरंजन, गेमिंग और सांस्कृतिक उद्देश्यों के लिए रचनात्मक नाम और पहचानकर्ता उत्पन्न करें',
            ar: 'أنشئ أسماء ومعرفات إبداعية لأغراض الترفيه والألعاب والثقافة',
            fr: 'Générez des noms et des identifiants créatifs à des fins de divertissement, de jeux et de culture',
            de: 'Generieren Sie kreative Namen und Identifikatoren für Unterhaltungs-, Gaming- und kulturelle Zwecke',
            ja: 'エンターテインメント、ゲーム、文化的な目的のために創造的な名前と識別子を生成します',
            tr: 'Eğlence, oyun ve kültürel amaçlar için yaratıcı isimler ve tanımlayıcılar üretin',
            ko: '엔터테인먼트, 게임 및 문화 목적으로 창의적인 이름과 식별자를 생성합니다',
            id: 'Hasilkan nama dan pengenal kreatif untuk tujuan hiburan, permainan, dan budaya',
            vi: 'Tạo tên và định danh sáng tạo cho các mục đích giải trí, trò chơi và văn hóa',
            ru: 'Генерируйте творческие имена и идентификаторы для развлекательных, игровых и культурных целей',
            it: 'Genera nomi e identificatori creativi per scopi di intrattenimento, giochi e cultura',
            pl: 'Generuj kreatywne nazwy i identyfikatory do celów rozrywkowych, gier i kultury',
            nl: 'Genereer creatieve namen en identifiers voor entertainment, gaming en culturele doeleinden',
        },
        icon: <Sparkles className="h-5 w-5" />,
        generators: [
            {
                id: 'game-names',
                name: {
                    en: 'Game Name Generator',
                    es: 'Generador de Nombres de Juegos',
                    pt: 'Gerador de Nomes de Jogos',
                    hi: 'गेम नाम जनरेटर',
                    ar: 'مولد أسماء الألعاب',
                    fr: 'Générateur de Noms de Jeux',
                    de: 'Spielenamen Generator',
                    ja: 'ゲーム名ジェネレーター',
                    tr: 'Oyun Adı Üreticisi',
                    ko: '게임 이름 생성기',
                    id: 'Generator Nama Game',
                    vi: 'Trình Tạo Tên Trò Chơi',
                    ru: 'Генератор Названий Игр',
                    it: 'Generatore di Nomi di Giochi',
                    pl: 'Generator Nazw Gier',
                    nl: 'Spelnaam Generator',
                },
                description: {
                    en: 'Generate catchy and creative names for video games, mobile apps, and RPG campaigns across various genres',
                    es: 'Genera nombres pegadizos y creativos para videojuegos, aplicaciones móviles y campañas de RPG en varios géneros',
                    pt: 'Gere nomes cativantes e criativos para videogames, aplicativos móveis e campanhas de RPG em vários gêneros',
                    hi: 'विभिन्न शैलियों में वीडियो गेम, मोबाइल ऐप और आरपीजी अभियानों के लिए आकर्षक और रचनात्मक नाम उत्पन्न करें',
                    ar: 'أنشئ أسماء جذابة وإبداعية لألعاب الفيديو وتطبيقات الهاتف المحمول وحملات RPG عبر مختلف الأنواع',
                    fr: 'Générez des noms accrocheurs et créatifs pour les jeux vidéo, les applications mobiles et les campagnes RPG dans divers genres',
                    de: 'Generieren Sie eingängige und kreative Namen für Videospiele, mobile Apps und RPG-Kampagnen in verschiedenen Genres',
                    ja: 'さまざまなジャンルのビデオゲーム、モバイルアプリ、RPGキャンペーンのためのキャッチーで創造的な名前を生成します',
                    tr: 'Çeşitli türlerde video oyunları, mobil uygulamalar ve RPG kampanyaları için akılda kalıcı ve yaratıcı isimler üretin',
                    ko: '다양한 장르의 비디오 게임, 모바일 앱 및 RPG 캠페인을 위한 눈길을 끄는 창의적인 이름 생성',
                    id: 'Hasilkan nama yang menarik dan kreatif untuk video game, aplikasi seluler, dan kampanye RPG di berbagai genre',
                    vi: 'Tạo tên hấp dẫn và sáng tạo cho trò chơi điện tử, ứng dụng di động và chiến dịch RPG trên nhiều thể loại',
                    ru: 'Генерируйте броские и креативные названия для видеоигр, мобильных приложений и кампаний RPG в различных жанрах',
                    it: 'Genera nomi accattivanti e creativi per videogiochi, app mobili e campagne RPG in vari generi',
                    pl: 'Generuj chwytliwe i kreatywne nazwy dla gier wideo, aplikacji mobilnych i kampanii RPG w różnych gatunkach',
                    nl: 'Genereer pakkende en creatieve namen voor videogames, mobiele apps en RPG-campagnes in verschillende genres',
                },
                icon: <Zap className="h-4 w-4" />,
                color: 'purple',
            },
            {
                id: 'fantasy-dnd-names',
                name: {
                    en: 'Fantasy & D&D Name Generator',
                    es: 'Generador de Nombres de Fantasía y D&D',
                    pt: 'Gerador de Nomes de Fantasia e D&D',
                    hi: 'फंतासी और D&D नाम जनरेटर',
                    ar: 'مولد أسماء الخيال و D&D',
                    fr: 'Générateur de Noms Fantastiques et D&D',
                    de: 'Fantasy- und D&D-Namen-Generator',
                    ja: 'ファンタジーとD&D名前ジェネレーター',
                    tr: 'Fantastik ve D&D İsim Üretici',
                    ko: '판타지 및 D&D 이름 생성기',
                    id: 'Generator Nama Fantasi dan D&D',
                    vi: 'Trình Tạo Tên Giả Tưởng và D&D',
                    ru: 'Генератор Фантазийных и D&D Имен',
                    it: 'Generatore di Nomi Fantasy e D&D',
                    pl: 'Generator Nazw Fantastycznych i D&D',
                    nl: 'Fantasie en D&D Naam Generator',
                },
                description: {
                    en: 'Generate magical names for fantasy characters and D&D races. Choose from D&D races (Elf, Dwarf, Tiefling, etc.) or Fantasy styles (Magic, Dragon).',
                    es: 'Genera nombres mágicos para personajes de fantasía y razas de D&D. Elige entre razas de D&D (Elfo, Enano, Tiefling, etc.) o estilos de Fantasía (Magia, Dragón).',
                    pt: 'Gere nomes mágicos para personagens de fantasia e raças de D&D. Escolha entre raças de D&D (Elfo, Anão, Tiefling, etc.) ou estilos de Fantasia (Magia, Dragão).',
                    hi: 'फंतासी पात्रों और D&D जातियों के लिए जादुई नाम उत्पन्न करें। D&D जातियों (एल्फ, ड्वार्फ, टिफ़लिंग, आदि) या फंतासी शैलियों (मैजिक, ड्रैगन) में से चुनें।',
                    ar: 'أنشئ أسماء سحرية لشخصيات الخيال وأجناس D&D. اختر من بين أجناس D&D (العفريت، القزم، التيفلينغ، إلخ) أو أنماط الخيال (السحر، التنين).',
                    fr: 'Générez des noms magiques pour les personnages fantastiques et les races D&D. Choisissez parmi les races D&D (Elfe, Nain, Tiefling, etc.) ou les styles Fantastiques (Magie, Dragon).',
                    de: 'Generieren Sie magische Namen für Fantasy-Charaktere und D&D-Rassen. Wählen Sie aus D&D-Rassen (Elf, Zwerg, Tiefling, etc.) oder Fantasy-Stilen (Magie, Drache).',
                    ja: 'ファンタジーキャラクターとD&D種族のための魔法的な名前を生成します。D&D種族（エルフ、ドワーフ、ティーフリングなど）またはファンタジースタイル（魔法、ドラゴン）から選択してください。',
                    tr: 'Fantastik karakterler ve D&D ırkları için büyülü isimler üretin. D&D ırkları (Elf, Cüce, Tiefling, vb.) veya Fantastik stiller (Büyü, Ejderha) arasından seçim yapın.',
                    ko: '판타지 캐릭터와 D&D 종족을 위한 마법적인 이름을 생성하세요. D&D 종족(엘프, 드워프, 티플링 등) 또는 판타지 스타일(마법, 드래곤) 중에서 선택하세요.',
                    id: 'Hasilkan nama ajaib untuk karakter fantasi dan ras D&D. Pilih dari ras D&D (Elf, Dwarf, Tiefling, dll.) atau gaya Fantasi (Sihir, Naga).',
                    vi: 'Tạo tên ma thuật cho các nhân vật giả tưởng và chủng tộc D&D. Chọn từ các chủng tộc D&D (Elf, Dwarf, Tiefling, v.v.) hoặc phong cách Giả tưởng (Ma thuật, Rồng).',
                    ru: 'Генерируйте магические имена для фэнтезийных персонажей и рас D&D. Выбирайте из рас D&D (Эльф, Гном, Тифлинг и т.д.) или фэнтезийных стилей (Магия, Дракон).',
                    it: 'Genera nomi magici per personaggi fantasy e razze D&D. Scegli tra razze D&D (Elfo, Nano, Tiefling, ecc.) o stili Fantasy (Magia, Drago).',
                    pl: 'Generuj magiczne imiona dla postaci fantasy i ras D&D. Wybierz spośród ras D&D (Elf, Krasnolud, Tiefling, itp.) lub stylów Fantasy (Magia, Smok).',
                    nl: 'Genereer magische namen voor fantasy personages en D&D rassen. Kies uit D&D rassen (Elf, Dwerg, Tiefling, enz.) of Fantasy stijlen (Magie, Draak).'
                },
                icon: <Sparkles className="h-4 w-4" />,
                color: 'emerald',
            },
            {
                id: 'mythology-names',
                name: {
                    en: 'Mythology Name Generator',
                    es: 'Generador de Nombres Mitológicos',
                    pt: 'Gerador de Nomes Mitológicos',
                    hi: 'पौराणिक नाम जनरेटर',
                    ar: 'مولد أسماء الأساطير',
                    fr: 'Générateur de Noms Mythologiques',
                    de: 'Mythologie-Namen-Generator',
                    ja: '神話名ジェネレーター',
                    tr: 'Mitoloji İsim Üretici',
                    ko: '신화 이름 생성기',
                    id: 'Generator Nama Mitologi',
                    vi: 'Trình Tạo Tên Thần Thoại',
                    ru: 'Генератор Мифологических Имен',
                    it: 'Generatore di Nomi Mitologici',
                    pl: 'Generator Nazw Mitologicznych',
                    nl: 'Mythologie Naam Generator',
                },
                description: {
                    en: 'Generate names from Greek, Norse, and Turkish mythology. Explore ancient names for your stories and games.',
                    es: 'Genera nombres de la mitología griega, nórdica y turca. Explora nombres antiguos para tus historias y juegos.',
                    pt: 'Gere nomes da mitologia grega, nórdica e turca. Explore nomes antigos para suas histórias e jogos.',
                    hi: 'ग्रीक, नॉर्स और तुर्की पौराणिक कथाओं से नाम उत्पन्न करें। अपनी कहानियों और खेलों के लिए प्राचीन नामों का अन्वेषण करें।',
                    ar: 'أنشئ أسماء من الأساطير اليونانية والنوردية والتركية. استكشف الأسماء القديمة لقصصك وألعابك.',
                    fr: 'Générez des noms de la mythologie grecque, nordique et turque. Explorez des noms anciens pour vos histoires et jeux.',
                    de: 'Generieren Sie Namen aus der griechischen, nordischen und türkischen Mythologie. Erkunden Sie alte Namen für Ihre Geschichten und Spiele.',
                    ja: 'ギリシャ、北欧、トルコの神話から名前を生成します。ストーリーやゲームのための古代の名前を探索してください。',
                    tr: 'Yunan, İskandinav ve Türk mitolojisinden isimler üretin. Hikayeleriniz ve oyunlarınız için kadim isimleri keşfedin.',
                    ko: '그리스, 북유럽, 터키 신화에서 이름을 생성하세요. 스토리와 게임을 위한 고대 이름을 탐색하세요.',
                    id: 'Hasilkan nama dari mitologi Yunani, Norse, dan Turki. Jelajahi nama kuno untuk cerita dan game Anda.',
                    vi: 'Tạo tên từ thần thoại Hy Lạp, Bắc Âu và Thổ Nhĩ Kỳ. Khám phá những cái tên cổ đại cho câu chuyện và trò chơi của bạn.',
                    ru: 'Генерируйте имена из греческой, скандинавской и турецкой мифологии. Исследуйте древние имена для ваших историй и игр.',
                    it: 'Genera nomi dalla mitologia greca, nordica e turca. Esplora nomi antichi per le tue storie e giochi.',
                    pl: 'Generuj imiona z mitologii greckiej, nordyckiej i tureckiej. Odkrywaj starożytne imiona dla swoich historii i gier.',
                    nl: 'Genereer namen uit de Griekse, Noorse en Turkse mythologie. Verken oude namen voor je verhalen en games.',
                },
                icon: <Sparkles className="h-4 w-4" />,
                color: 'violet',
            },
            {
                id: 'horror-names',
                name: {
                    en: 'Horror Name Generator',
                    es: 'Generador de Nombres de Terror',
                    pt: 'Gerador de Nomes de Terror',
                    hi: 'डरावने नाम जनरेटर',
                    ar: 'مولد أسماء الرعب',
                    fr: 'Générateur de Noms d\'Horreur',
                    de: 'Horror-Namen-Generator',
                    ja: 'ホラーネームジェネレーター',
                    tr: 'Korku İsim Üretici',
                    ko: '공포 이름 생성기',
                    id: 'Generator Nama Horor',
                    vi: 'Trình Tạo Tên Kinh Dị',
                    ru: 'Генератор Имен Ужасов',
                    it: 'Generatore di Nomi Horror',
                    pl: 'Generator Nazw Horroru',
                    nl: 'Horror Naam Generator',
                },
                description: {
                    en: 'Generate terrifying names for horror stories and games. Choose from Demon, Slasher, and Cosmic Horror styles.',
                    es: 'Genera nombres aterradores para historias y juegos de terror. Elige entre estilos Demonio, Slasher y Horror Cósmico.',
                    pt: 'Gere nomes aterrorizantes para histórias e jogos de terror. Escolha entre estilos Demônio, Slasher e Horror Cósmico.',
                    hi: 'डरावनी कहानियों और खेलों के लिए भयानक नाम उत्पन्न करें। डेमन, स्लैशर और कॉस्मिक होरर शैलियों में से चुनें।',
                    ar: 'أنشئ أسماء مرعبة لقصص وألعاب الرعب. اختر من بين أنماط الشيطان والسلاشر والرعب الكوني.',
                    fr: 'Générez des noms terrifiants pour des histoires et des jeux d\'horreur. Choisissez parmi les styles Démon, Slasher et Horreur Cosmique.',
                    de: 'Generieren Sie erschreckende Namen für Horrorgeschichten und -spiele. Wählen Sie aus Dämon-, Slasher- und Kosmischer Horror-Stilen.',
                    ja: 'ホラーストーリーやゲームのための恐ろしい名前を生成します。デーモン、スラッシャー、コズミックホラーのスタイルから選択してください。',
                    tr: 'Korku hikayeleri ve oyunları için ürkütücü isimler üretin. Şeytan, Slasher ve Kozmik Korku stilleri arasından seçin.',
                    ko: '공포 스토리와 게임을 위한 무시무시한 이름을 생성합니다. 악마, 슬래셔, 코스믹 호러 스타일 중에서 선택하세요.',
                    id: 'Hasilkan nama menakutkan untuk cerita dan game horor. Pilih dari gaya Iblis, Slasher, dan Horror Kosmik.',
                    vi: 'Tạo tên đáng sợ cho các câu chuyện và trò chơi kinh dị. Chọn từ các phong cách Quỷ, Slasher và Kinh dị Vũ trụ.',
                    ru: 'Генерируйте ужасающие имена для историй и игр в жанре ужасов. Выбирайте из стилей Демон, Слэшер и Космический Ужас.',
                    it: 'Genera nomi terrificanti per storie e giochi horror. Scegli tra stili Demone, Slasher e Horror Cosmico.',
                    pl: 'Generuj przerażające imiona dla horrorów i gier. Wybierz spośród stylów Demon, Slasher i Kosmiczny Horror.',
                    nl: 'Genereer angstaanjagende namen voor horrorverhalen en games. Kies uit Demon, Slasher en Kosmische Horror stijlen.',
                },
                icon: <Skull className="h-4 w-4" />,
                color: 'red',
            },
            {
                id: 'funny-names',
                name: {
                    en: 'Funny / Absurd Name Generator',
                    es: 'Generador de Nombres Divertidos / Absurdos',
                    pt: 'Gerador de Nomes Engraçados / Absurdos',
                    hi: 'मजेदार / बेतुका नाम जनरेटर',
                    ar: 'مولد الأسماء المضحكة / السخيفة',
                    fr: 'Générateur de Noms Drôles / Absurdes',
                    de: 'Lustiger / Absurder Namensgenerator',
                    ja: '面白い / 不条理な名前ジェネレーター',
                    tr: 'Komik / Absürt İsim Üretici',
                    ko: '재미있는 / 터무니없는 이름 생성기',
                    id: 'Generator Nama Lucu / Mustahil',
                    vi: 'Trình Tạo Tên Vui Nhộn / Vô Lý',
                    ru: 'Генератор Смешных / Абсурдных Имен',
                    it: 'Generatore di Nomi Divertenti / Assurdi',
                    pl: 'Generator Śmiesznych / Absurdalnych Imion',
                    nl: 'Grappige / Absurde Naam Generator',
                },
                description: {
                    en: 'Generate hilarious, weird, and absolutely nonsensical names. Perfect for comedy, pranks, or just for fun.',
                    es: 'Genera nombres divertidísimos, extraños y absolutamente sin sentido. Perfecto para comedia, bromas o simplemente por diversión.',
                    pt: 'Gere nomes hilários, estranhos e absolutamente sem sentido. Perfeito para comédia, pegadinhas ou apenas por diversão.',
                    hi: 'हास्यास्पद, अजीब और पूरी तरह से बेतुके नाम उत्पन्न करें। कॉमेडी, मज़ाक या बस मनोरंजन के लिए बिल्कुल सही।',
                    ar: 'قم بإنشاء أسماء مرحة وغريبة وغير منطقية تمامًا. مثالية للكوميديا ​​أو المقالب أو لمجرد التسلية.',
                    fr: 'Générez des noms hilarants, bizarres et absolument absurdes. Parfait pour la comédie, les farces ou juste pour le plaisir.',
                    de: 'Generieren Sie lustige, seltsame und absolut unsinnige Namen. Perfekt für Comedy, Streiche oder einfach nur zum Spaß.',
                    ja: '陽気で奇妙で、まったくナンセンスな名前を生成します。コメディ、いたずら、または単なる楽しみに最適です。',
                    tr: 'Son derece komik, tuhaf ve tamamen anlamsız isimler üretin. Komedi, şakalar veya sadece eğlence için mükemmel.',
                    ko: '재미있고 상하며 완전히 터무니없는 이름을 생성하세요. 코미디, 장난 또는 단순한 재미를 위해 완벽합니다.',
                    id: 'Hasilkan nama-nama lucu, aneh, dan benar-benar tidak masuk akal. Sempurna untuk komedi, lelucon, atau hanya untuk bersenang-senang.',
                    vi: 'Tạo ra những cái tên vui nhộn, kỳ lạ và hoàn toàn vô nghĩa. Hoàn hảo cho hài kịch, trò chơi khăm hoặc chỉ để cho vui.',
                    ru: 'Генерируйте веселые, странные и совершенно бессмысленные имена. Идеально подходит для комедии, розыгрышей или просто для развлечения.',
                    it: 'Genera nomi esilaranti, strani e assolutamente privi di senso. Perfetto per commedie, scherzi o semplicemente per divertimento.',
                    pl: 'Generuj zabawne, dziwne i całkowicie bezsensowne imiona. Idealne do komedii, figli lub po prostu dla zabawy.',
                    nl: 'Genereer hilarische, rare en absoluut onzinnige namen. Perfect voor komedie, grappen of gewoon voor de lol.',
                },
                icon: <Sparkles className="h-4 w-4" />, // Using Sparkles as a placeholder, maybe a different icon would be better if available like Smile but reusing imports
                color: 'orange',
            },
            {
                id: 'medieval-names',
                name: {
                    en: 'Medieval Name Generator',
                    es: 'Generador de Nombres Medievales', 
                    pt: 'Gerador de Nomes Medievais', 
                    hi: 'मध्यकालीन नाम जनरेटर', 
                    ar: 'مولد أسماء القرون الوسطى', 
                    fr: 'Générateur de Noms Médiévaux',
                    de: 'Mittelalterlicher Namensgenerator', 
                    ja: '中世の名前ジェネレーター', 
                    tr: 'Orta Çağ İsim Üretici',
                    ko: '중세 이름 생성기', 
                    id: 'Generator Nama Abad Pertengahan', 
                    vi: 'Trình Tạo Tên Trung Cổ', 
                    ru: 'Генератор Средневековых Имён', 
                    it: 'Generatore di Nomi Medievali', 
                    pl: 'Generator Średniowiecznych Imion', 
                    nl: 'Middeleeuwse Naamgenerator', 
                },
                description: {
                    en: 'Generate authentic medieval names for peasants, nobles, and knights. Travel back in time for your stories and RPGs.',
                    es: 'Genera nombres medievales auténticos para campesinos, nobles y caballeros. Viaja en el tiempo para tus historias y RPGs.',
                    pt: 'Gere nomes medievais autênticos para camponeses, nobres e cavaleiros. Viaje no tempo para suas histórias e RPGs.',
                    hi: 'किसानों, रईसों और शूरवीरों के लिए प्रामाणिक मध्यकालीन नाम बनाएं। अपनी कहानियों और RPG के लिए समय में यात्रा करें।',
                    ar: 'أنشئ أسماء قرون وسطى أصيلة للفلاحين والنبلاء والفرسان. سافر عبر الزمن لقصصك وألعاب تقمص الأدوار.',
                    fr: 'Générez des noms médiévaux authentiques pour paysans, nobles et chevaliers. Voyagez dans le temps pour vos histoires et RPG.',
                    de: 'Generiere authentische mittelalterliche Namen für Bauern, Adlige und Ritter. Reise in die Vergangenheit für deine Geschichten und RPGs.',
                    ja: '農民、貴族、騎士のための本格的な中世の名前を生成します。物語やRPGのために時間を遡りましょう。',
                    tr: 'Köylüler, soylular ve şövalyeler için otantik Orta Çağ isimleri oluşturun. Hikayeleriniz ve RPG\'leriniz için zamanda yolculuğa çıkın.',
                    ko: '농민, 귀족, 기사를 위한 정통 중세 이름을 생성하세요. 이야기와 RPG를 위해 시간 여행을 떠나세요.',
                    id: 'Hasilkan nama-nama abad pertengahan autentik untuk petani, bangsawan, dan ksatria. Berpetualang melintasi waktu untuk cerita dan RPG Anda.',
                    vi: 'Tạo tên trung cổ chân thực cho nông dân, quý tộc và hiệp sĩ. Du hành ngược thời gian cho câu chuyện và RPG của bạn.',
                    ru: 'Генерируйте аутентичные средневековые имена для крестьян, дворян и рыцарей. Путешествуйте во времени для ваших историй и RPG.',
                    it: 'Genera nomi medievali autentici per contadini, nobili e cavalieri. Viaggia indietro nel tempo per le tue storie e RPG.',
                    pl: 'Generuj autentyczne średniowieczne imiona dla chłopów, szlachciców i rycerzy. Podróżuj w czasie dla swoich historii i RPG.',
                    nl: 'Genereer authentieke middeleeuwse namen voor boeren, edelen en ridders. Reis terug in de tijd voor je verhalen en RPG\'s.',
                },
                icon: <Shield className="h-4 w-4" />,
                color: 'amber',
            },
            // {
            //     id: 'pet-names',
            //     name: 'Pet Names',
            //     description: 'Names for cats, dogs, birds, and other pets',
            //     icon: <Heart className="h-4 w-4" />,
            //     color: 'pink',
            // },
            // {
            //     id: 'movie-character-names',
            //     name: 'Movie/TV Character Names',
            //     description: 'Character-style names for films and series',
            //     icon: <Film className="h-4 w-4" />,
            //     color: 'orange',
            // },
            // {
            //     id: 'nicknames',
            //     name: 'Nicknames',
            //     description: 'GenZ, humorous, cool nicknames',
            //     icon: <UserCheck className="h-4 w-4" />,
            //     color: 'green',
            // },
            // {
            //     id: 'superhero-names',
            //     name: 'Superhero Names',
            //     description: 'Epic superhero and villain names for comics and gaming',
            //     icon: <Zap className="h-4 w-4" />,
            //     color: 'yellow',
            // },
            {
                id: 'wild-west-names',
                name: {
                    en: 'Wild West Name Generator',
                    es: 'Generador de Nombres del Lejano Oeste',
                    pt: 'Gerador de Nomes do Velho Oeste',
                    hi: 'वाइल्ड वेस्ट नाम जनरेटर',
                    ar: 'مولد أسماء الغرب المتوحش',
                    fr: 'Générateur de Noms du Far West',
                    de: 'Wild West Namensgenerator',
                    ja: 'ワイルドウェストネームジェネレーター',
                    tr: 'Vahşi Batı İsim Üretici',
                    ko: '와일드 웨스트 이름 생성기',
                    id: 'Generator Nama Wild West',
                    vi: 'Trình Tạo Tên Miền Tây Hoang Dã',
                    ru: 'Генератор Имен Дикого Запада',
                    it: 'Generatore di Nomi del Vecchio West',
                    pl: 'Generator Imion Dzikiego Zachodu',
                    nl: 'Wilde Westen Naam Generator',
                },
                description: {
                    en: 'Generate rugged and authentic names from the American Frontier. Perfect for western stories, games, and roleplay.',
                    es: 'Genera nombres rudos y auténticos de la Frontera Americana. Perfecto para historias del oeste, juegos y juegos de rol.',
                    pt: 'Gere nomes rudes e autênticos da Fronteira Americana. Perfeito para histórias de faroeste, jogos e RPG.',
                    hi: 'अमेरिकी सीमा से कठोर और प्रामाणिक नाम उत्पन्न करें। पश्चिमी कहानियों, खेलों और रोलप्ले के लिए बिल्कुल सही।',
                    ar: 'قم بإنشاء أسماء قوية وأصلية من الحدود الأمريكية. مثالي للقصص الغربية والألعاب وتقمص الأدوار.',
                    fr: 'Générez des noms rudes et authentiques de la frontière américaine. Parfait pour les histoires de western, les jeux et les jeux de rôle.',
                    de: 'Generieren Sie raue und authentische Namen von der amerikanischen Grenze. Perfekt für Westerngeschichten, Spiele und Rollenspiele.',
                    ja: 'アメリカのフロンティアからの無骨で本物の名前を生成します。西部劇、ゲーム、ロールプレイに最適です。',
                    tr: 'Amerikan Sınırı\'ndan sert ve otantik isimler üretin. Western hikayeleri, oyunlar ve rol yapma oyunları için mükemmel.',
                    ko: '미국 개척 시대의 거칠고 진정한 이름을 생성하세요. 서부 이야기, 게임 및 롤플레잉에 완벽합니다.',
                    id: 'Hasilkan nama-nama tangguh dan otentik dari Perbatasan Amerika. Sempurna untuk cerita barat, game, dan roleplay.',
                    vi: 'Tạo ra những cái tên mạnh mẽ và chân thực từ Biên giới Hoa Kỳ. Hoàn hảo cho các câu chuyện phương Tây, trò chơi và nhập vai.',
                    ru: 'Генерируйте суровые и аутентичные имена с американского фронтира. Идеально подходит для вестернов, игр и ролевых игр.',
                    it: 'Genera nomi rudi e autentici della frontiera americana. Perfetto per storie western, giochi e giochi di ruolo.',
                    pl: 'Generuj surowe i autentyczne imiona z amerykańskiego pogranicza. Idealne do westernów, gier i odgrywania ról.',
                    nl: 'Genereer ruige en authentieke namen van de Amerikaanse Frontier. Perfect voor westernverhalen, games en rollenspellen.',
                },
                icon: <Sparkles className="h-4 w-4" />,
                color: 'amber',
            },
        ]
    },
    // {
    //     id: 'business-financial',
    //     name: 'Business & Financial',
    //     description: 'Generate business and financial data for testing, development and prototyping',
    //     icon: <Building className="h-5 w-5" />,
    //     generators: [
    //         {
    //             id: 'business-names',
    //             name: 'Business Names',
    //             description: 'Company names, brand names',
    //             icon: <Building className="h-4 w-4" />,
    //         },
    //         {
    //             id: 'iban-numbers',
    //             name: 'IBAN Numbers',
    //             description: 'International Bank Account Numbers',
    //             icon: <Banknote className="h-4 w-4" />,
    //         },
    //         {
    //             id: 'credit-card-numbers',
    //             name: 'Credit Card Numbers',
    //             description: 'Various card types (Visa, MasterCard, etc.)',
    //             icon: <CreditCard className="h-4 w-4" />,
    //         },
    //         {
    //             id: 'bank-account-numbers',
    //             name: 'Bank Account Numbers',
    //             description: 'Account and routing numbers',
    //             icon: <Banknote className="h-4 w-4" />,
    //         },
    //         {
    //             id: 'tax-ids',
    //             name: 'Tax IDs',
    //             description: 'Social Security Numbers, EIN, VAT numbers',
    //             icon: <Hash className="h-4 w-4" />,
    //         }
    //     ]
    // },
    // {
    //     id: 'digital-technical',
    //     name: 'Digital & Technical',
    //     description: 'Generate technical and digital identifiers for testing, development and prototyping',
    //     icon: <Terminal className="h-5 w-5" />,
    //     generators: [
    //         {
    //             id: 'ip-addresses',
    //             name: 'IP Addresses',
    //             description: 'IPv4 and IPv6 addresses',
    //             icon: <Globe className="h-4 w-4" />,
    //         },
    //         {
    //             id: 'mac-addresses',
    //             name: 'MAC Addresses',
    //             description: 'Network interface addresses',
    //             icon: <Terminal className="h-4 w-4" />,
    //         },
    //         {
    //             id: 'uuids', // Universally Unique Identifiers
    //             name: 'UUIDs',
    //             description: 'Universally unique identifiers',
    //             icon: <Hash className="h-4 w-4" />,
    //         },
    //         {
    //             id: 'passwords', // Passwords
    //             name: 'Passwords',
    //             description: 'Secure password generation',
    //             icon: <Shield className="h-4 w-4" />,
    //         },
    //         {
    //             id: 'usernames', // Usernames
    //             name: 'Usernames',
    //             description: 'Random username generation',
    //             icon: <Key className="h-4 w-4" />,
    //         }
    //     ]
    // }
};

export const POPULAR_GENERATORS: IGenerator[] = [
    CATEGORIES.personal.generators[0], // Fake Name Generator
    CATEGORIES.entertainment.generators[0], // Game Name Generator
    CATEGORIES.entertainment.generators[1], // Fantasy & D&D Name Generator (unified)
    CATEGORIES.entertainment.generators[3], // Horror Name Generator
    CATEGORIES.entertainment.generators[2], // Mythology Name Generator
    CATEGORIES.entertainment.generators[4], // Funny / Absurd Name Generator
    CATEGORIES.entertainment.generators[5], // Medieval Name Generator
    CATEGORIES.entertainment.generators[6], // Wild West Name Generator
];

export const POPULAR_DATA_GENERATORS: IGenerator[] = [
    // CATEGORIES.personal.generators[1], // Fake Address Generator (hidden)
    // CATEGORIES.personal.generators[2], // Fake Email Generator (hidden)
    CATEGORIES.personal.generators[1], // IP Address Generator (was index 3, now 1)
    CATEGORIES.personal.generators[2], // Password Generator (was index 4, now 2)
];